import React, { useState, useEffect, memo, useMemo } from 'react';
import HoneyCostPopup from '../../components/honeyFarm/HoneyCostPopup';
import rarities from '../../assets/Data/hive_rarities';
import SelectedHive from '../SelectedHive';
import { useHoneyFarm } from '../../context/HoneyFarmContext';

interface IHiveSelectorProps {
  hiveIds: number[];
  txPending: boolean;
  setTxPending: (pending: boolean) => void;
  isDesktop: boolean;
  addressHoney: number;
  address: string | undefined | null;
  addressHiveTokenIds: number[];
}

const combCost = [10, 17.5, 25, 32.5, 40];
const MAX_COMBS_PER_HIVE = 2;

const normalizeToNumber = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value);
  if (value && typeof value.toNumber === 'function') return value.toNumber();
  if (value && typeof value.toString === 'function') {
    const parsed = Number(value.toString());
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Define types for the hives tree data
interface HiveMapping {
  leaf: string;
  proof: string[];
  id: number;
}

interface HivesTreeData {
  root: string;
  mappings: HiveMapping[];
}

const HiveSelector: React.FC<IHiveSelectorProps> = (props) => {
  const { claimHoneyComb, refreshHoneycombStates, honeycombs, loadResource } = useHoneyFarm();
  const [sortedHives, setSortedHives] = useState<number[][]>([[], [], [], [], []]);
  const [hiveCombCount, setHiveCombCount] = useState<{ [id: number]: number }>({});
  const [totalHoney, setTotalHoney] = useState<number[]>([0, 0, 0, 0, 0]);
  const [hivesTree, setHivesTree] = useState<HivesTreeData | null>(null);

  useEffect(() => {
    loadResource('honeycombs').catch((error) => console.error('Failed to load honeycomb balances', error));
  }, [loadResource]);

  // Load hives tree data asynchronously
  useEffect(() => {
    const loadHivesTree = async () => {
      try {
        const response = await fetch('/trees/hives.json');
        const data: HivesTreeData = await response.json();
        setHivesTree(data);
      } catch (error) {
        console.error('Failed to load hives tree data:', error);
      }
    };
    
    loadHivesTree();
  }, []);

  useEffect(() => {
    // Sort hives by rarity - ported from original bears logic with handling for unknown hives
    let _sortedHives: number[][] = [[], [], [], [], []];
    props.hiveIds.forEach((id) => {
      // Find rarity for this hive ID
      const rarityEntry = rarities.find((rarity) => Number(rarity.id) === Number(id));
      let rarityIndex: number;

      if (rarityEntry) {
        // Use defined rarity from data (rarity values are 0-4, map to indices 0-4)
        rarityIndex = rarityEntry.rarity;
      } else {
        // For hives not in the rarity data, assign to Common (index 0)
        rarityIndex = 0;
      }

      if (rarityIndex >= 0 && rarityIndex <= 4) {
        _sortedHives[rarityIndex].push(Number(id));
      }
    });

    // Log distribution summary
    const distribution = _sortedHives.map((arr, i) => `${rarityToTitle(i)}: ${arr.length}`).join(', ');
    console.log(`HiveSelector: ${props.hiveIds.length} hives distributed as: ${distribution}`);

    setSortedHives(_sortedHives);
  }, [props.hiveIds]);

  useEffect(() => {
    if (!props.txPending) {
      setHiveCombCount({});
      setTotalHoney([0, 0, 0, 0, 0]);
    }
  }, [props.txPending]);

  const hiveClaimedMap = useMemo(() => {
    const map: Record<number, number> = {};
    honeycombs?.forEach((entry: any) => {
      map[Number(entry.hiveId)] = normalizeToNumber(entry.claimed ?? 0);
    });
    return map;
  }, [honeycombs]);

  // Update honey cost for comb extraction
  const updateHoneyCost = (rarityIndex: number, add: boolean) => {
    let _honey = totalHoney[rarityIndex];
    _honey = add ? _honey + combCost[rarityIndex] : _honey - combCost[rarityIndex];
    let _th = Array.from(totalHoney);
    _th[rarityIndex] = _honey;
    setTotalHoney(_th);
  };

  // Get total combs for a hive array
  const getTotalCombs = (hiveArray: number[]) => {
    let total = 0;
    hiveArray.forEach((_id) => {
      if (hiveCombCount[_id] !== undefined && hiveCombCount[_id] !== null) {
        total += hiveCombCount[_id];
      }
    });
    return total;
  };

  // Claim combs for a given rarity
  const claimCombs = async (rarity: number) => {
    const hiveIdsToClaim: number[] = [];
    const leafs: string[] = [];
    const proofs: string[][] = [];
    const quantities: number[] = [];
    
    sortedHives[rarity].forEach((_id: number) => {
      if (hiveCombCount[_id] > 0 && hivesTree) {
        // Look for merkle data in the tree
        let mapping = hivesTree.mappings.find((m: HiveMapping) => m.id === _id);
        
        if (mapping) {
          hiveIdsToClaim.push(_id);
          leafs.push(mapping.leaf);
          proofs.push(mapping.proof);
          quantities.push(hiveCombCount[_id]);
        }
      }
    });
    
    if (hiveIdsToClaim.length === 0) return;
    
    props.setTxPending(true);
    try {
      if (claimHoneyComb) {
        await claimHoneyComb(hiveIdsToClaim, quantities, rarity, leafs, proofs);
        await refreshHoneycombStates();
      }
    } catch (error) {
      console.error('Error claiming honeycombs:', error);
    }
    props.setTxPending(false);
  };

  const getBorderStyle = (isDesktop: boolean): React.CSSProperties => ({
    borderRadius: '0.375rem',
    marginBottom: '10px',
    padding: '1em',
    justifyContent: isDesktop ? 'left' : 'center',
  });

  const rarityToTitle = (rarity: number) => {
    switch (rarity) {
      case 0:
        return 'Common';
      case 1:
        return 'Uncommon';
      case 2:
        return 'Rare';
      case 3:
        return 'Epic';
      case 4:
        return 'Legendary';
      default:
        return 'Common';
    }
  };

  // Show loading state while hives tree is loading
  if (!hivesTree) {
    return (
      <div className="w-full flex flex-col items-center justify-center p-8 bg-black bg-opacity-60 rounded-md border border-[#55bac3]">
        <div className="animate-pulse text-[#c4f6f9]">Loading hive data...</div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      {sortedHives.map((hiveArray, index) => {
        if (hiveArray.length >= 0) {
          return (
            <div key={index} className="w-full flex flex-row justify-center mb-6 bg-black bg-opacity-60 border border-[#55bac3] rounded-md" style={getBorderStyle(props.isDesktop)}>
              <div className="flex flex-col items-center mr-8">
                <div className="text-[#c4f6f9] text-sm mb-2">{rarityToTitle(index)} Hives</div>
                <HoneyCostPopup
                  requiredHoney={totalHoney[index]}
                  addressHoney={props.addressHoney}
                  callback={() => claimCombs(index)}
                  callBackArgs={[index]}
                  trigger={
                    <button
                      className={`px-6 py-2 bg-transparent border-2 border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3] hover:text-black text-sm rounded-md shadow mt-2 mb-2 transition-colors ${getTotalCombs(hiveArray) > 0 ? '' : 'opacity-50 cursor-not-allowed border-gray-600 text-gray-600 hover:bg-transparent hover:text-gray-600'}`}
                      disabled={getTotalCombs(hiveArray) === 0}
                    >
                      Extract
                    </button>
                  }
                />
                <div className="text-[#fcd34d] text-sm mt-2">Selected: {getTotalCombs(hiveArray)}</div>
              </div>
              <div className={`flex flex-row flex-wrap items-start ${props.isDesktop ? 'justify-start' : 'justify-center'}`} style={{marginTop: '10px'}}>
                {hiveArray.map((id, _index) => (
                  <div key={_index} className={`mr-4 mb-3`} style={{ width: props.isDesktop ? '180px' : '160px' }}>
                    <SelectedHive
                      hiveId={id}
                      address={props.address}
                      addressHiveTokenIds={props.addressHiveTokenIds}
                      addressHoney={props.addressHoney}
                      combCount={hiveCombCount}
                      setCombCount={setHiveCombCount}
                      txPending={props.txPending}
                      updateHoneyCost={updateHoneyCost}
                      rarityIndex={index}
                      claimedCombs={hiveClaimedMap[id] ?? 0}
                      maxCombs={MAX_COMBS_PER_HIVE}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

export default memo(HiveSelector, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    JSON.stringify(prevProps.hiveIds) === JSON.stringify(nextProps.hiveIds) &&
    prevProps.txPending === nextProps.txPending &&
    prevProps.isDesktop === nextProps.isDesktop &&
    prevProps.addressHoney === nextProps.addressHoney &&
    prevProps.address === nextProps.address &&
    JSON.stringify(prevProps.addressHiveTokenIds) === JSON.stringify(nextProps.addressHiveTokenIds)
  );
});
